import * as React from 'react';

import { keyframes } from '@emotion/react';
import { Button, IconButton, InputBase, Typography } from '@mui/material';
import { FileText, Pencil } from 'lucide-react';

import { type ApprovalResponseHandler } from '@modules/_shared/components/chat/ApprovalActions';
import { ChatImagePreviewDialog } from '@modules/_shared/components/chat/ChatImagePreviewDialog';
import {
  ChatSteps,
  ThinkingIndicator,
} from '@modules/_shared/components/chat/ChatSteps';
import { ChatToolCall } from '@modules/_shared/components/chat/ChatToolCall';
import { type ComposeEmailResultHandler } from '@modules/_shared/components/chat/composeEmailCard';
import { groupParts } from '@modules/_shared/components/chat/messageSegments';
import { StreamingMarkdown } from '@modules/_shared/components/chat/StreamingMarkdown';
import { useVisualChatId } from '@modules/_shared/components/chat/VisualToolCard';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

export type MessagePart = {
  type: string;
  text?: string;
  state?: string;
  toolName?: string;
  toolCallId?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  approval?: { id: string; approved?: boolean; reason?: string };
  // File parts (attachments) — data URLs while composing/streaming, signed
  // GCS read URLs when a persisted conversation is reloaded.
  url?: string;
  mediaType?: string;
  filename?: string;
  attachmentId?: string;
};

export const getMessageText = (parts: Array<MessagePart>): string => {
  return parts
    .filter(p => p.type === 'text')
    .map(p => p.text ?? '')
    .join('');
};

const chatMsgFadein = {
  animation: 'chatmsg-fadein 0.2s ease-out',
  '@keyframes chatmsg-fadein': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
} as const;

// Pulsing dot shown while the assistant is streaming — the streaming
// affordance (replaces the inline markdown caret).
const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.35); }
`;

const StreamingIndicator = () => {
  const { colors } = useTheme();
  const { t } = useLocale();
  return (
    <span
      role="status"
      aria-label={t('Generating')}
      css={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 14,
        lineHeight: 0,
      }}
    >
      <span
        css={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: colors.primaryMain,
          animation: `${pulse} 1s infinite ease-in-out`,
        }}
      />
    </span>
  );
};

// Browsers block top-frame navigation to `data:` URLs, so a fresh (not yet
// persisted) attachment can't be opened via a plain <a href="data:...">.
// Decode it into a Blob and open the object URL instead.
const dataUrlToObjectUrl = (dataUrl: string): string | null => {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) {
    return null;
  }
  const header = dataUrl.slice('data:'.length, comma);
  if (!header.includes(';base64')) {
    return null;
  }
  try {
    const binary = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const mediaType = header.split(';')[0] || 'application/octet-stream';
    return URL.createObjectURL(new Blob([bytes], { type: mediaType }));
  } catch {
    return null;
  }
};

// Attachment chip/thumbnail for a file part (image preview with full-screen
// lightbox, generic chip for PDFs and other documents). Also used by the
// admin conversation inspector (settings/chat-conversations).
export const FilePartView = ({ part }: { part: MessagePart }) => {
  const { colors } = useTheme();
  const chatId = useVisualChatId();
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  // Lazily-created Blob URL for opening a data-URL attachment; revoked on
  // unmount so long chats with many attachments don't leak memory.
  const objectUrlRef = React.useRef<string | null>(null);
  React.useEffect(
    () => () => {
      if (objectUrlRef.current != null) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    },
    [],
  );
  const isImage = (part.mediaType ?? '').startsWith('image/');
  if (isImage && part.url) {
    return (
      <>
        <img
          src={part.url}
          alt={part.filename ?? 'attachment'}
          onClick={() => setPreviewUrl(part.url ?? null)}
          css={{
            maxWidth: 180,
            maxHeight: 140,
            borderRadius: 8,
            border: `1px solid ${colors.divider}`,
            objectFit: 'cover',
            cursor: 'zoom-in',
          }}
        />
        <ChatImagePreviewDialog
          src={previewUrl}
          alt={part.filename}
          onClose={() => setPreviewUrl(null)}
          chatId={chatId}
          attachmentId={part.attachmentId}
          filename={part.filename}
        />
      </>
    );
  }
  // Persisted attachments come back from the API with a short-lived signed
  // https URL — make the chip a link so PDFs/documents can be opened from
  // history. Fresh (data-URL) attachments open through a Blob URL on click.
  const url = part.url ?? '';
  const href = /^https?:/i.test(url) ? part.url : undefined;
  const chipCss = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 8,
    border: `1px solid ${colors.divider}`,
    backgroundColor: colors.grey100,
    fontSize: '0.75rem',
    color: colors.grey700,
    maxWidth: 220,
    overflow: 'hidden',
  } as const;
  const chipContent = (
    <>
      <FileText size={14} style={{ flexShrink: 0 }} />
      <span css={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {part.filename ?? part.mediaType ?? 'file'}
      </span>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        css={{ ...chipCss, textDecoration: 'none', cursor: 'pointer' }}
      >
        {chipContent}
      </a>
    );
  }
  if (url.startsWith('data:')) {
    return (
      <button
        type="button"
        onClick={() => {
          objectUrlRef.current ??= dataUrlToObjectUrl(url);
          if (objectUrlRef.current != null) {
            window.open(objectUrlRef.current, '_blank', 'noopener');
          }
        }}
        css={{
          ...chipCss,
          fontFamily: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        {chipContent}
      </button>
    );
  }
  return <span css={chipCss}>{chipContent}</span>;
};

type ChatMessageProps = {
  parts: Array<MessagePart>;
  isUser: boolean;
  isStreaming: boolean;
  onApprovalResponse?: ApprovalResponseHandler;
  // Whether this is the chat's last turn and that turn is in flight. Only the
  // last turn's clarifying questions can still wait for an answer (given in
  // the card docked above the chat input).
  isLastTurn?: boolean;
  isLoading?: boolean;
  onComposeEmailResult?: ComposeEmailResultHandler;
  isMobile?: boolean;
  // Editing a user message truncates the conversation at that point and
  // regenerates from the edited text (undo of everything after it).
  onEditSubmit?: (text: string) => void;
  canEdit?: boolean;
};

const ChatMessageInner = ({
  parts,
  isUser,
  isStreaming,
  onApprovalResponse,
  isLastTurn = false,
  isLoading = false,
  onComposeEmailResult,
  isMobile = false,
  onEditSubmit,
  canEdit = false,
}: ChatMessageProps) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  if (isUser) {
    const text = getMessageText(parts);
    const fileParts = parts.filter(p => p.type === 'file');

    if (isEditing) {
      return (
        <div
          css={{
            backgroundColor: colors.white,
            borderRadius: 10,
            padding: '10px 14px',
            border: `1px solid ${colors.primaryMain}`,
            ...chatMsgFadein,
          }}
        >
          <InputBase
            multiline
            autoFocus
            fullWidth
            minRows={1}
            maxRows={8}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (draft.trim().length > 0) {
                  setIsEditing(false);
                  onEditSubmit?.(draft.trim());
                }
              }
              if (e.key === 'Escape') {
                setIsEditing(false);
              }
            }}
            sx={{ '& .MuiInputBase-input': { p: 0 } }}
          />
          <div
            css={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 8,
            }}
          >
            <Button
              size="small"
              color="inherit"
              onClick={() => setIsEditing(false)}
            >
              {t('Cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={draft.trim().length === 0}
              onClick={() => {
                setIsEditing(false);
                onEditSubmit?.(draft.trim());
              }}
            >
              {t('Send')}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div
        css={{
          position: 'relative',
          backgroundColor: colors.white,
          borderRadius: 10,
          padding: '10px 14px',
          border: `1px solid ${colors.divider}`,
          ...chatMsgFadein,
          '&:hover .chatmsg-edit': { opacity: 1 },
        }}
      >
        {fileParts.length > 0 && (
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: text.length > 0 ? 8 : 0,
            }}
          >
            {fileParts.map((part, i) => (
              <FilePartView key={`file-${i}`} part={part} />
            ))}
          </div>
        )}
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
        >
          {text}
        </Typography>
        {canEdit && onEditSubmit != null && (
          <IconButton
            className="chatmsg-edit"
            size="small"
            title={t('Edit message')}
            onClick={() => {
              setDraft(text);
              setIsEditing(true);
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0,
              transition: 'opacity 0.15s',
              backgroundColor: colors.white,
              '&:hover': { backgroundColor: colors.grey100 },
            }}
          >
            <Pencil size={13} color={colors.grey600} />
          </IconButton>
        )}
      </div>
    );
  }

  const segments = groupParts(parts);
  const lastSegment = segments[segments.length - 1];
  // An active steps block's header is already the "working" indicator; the
  // dot only marks answer text (or a file/card) still streaming.
  const trailingIndicator = !isStreaming ? null : lastSegment == null ? (
    <ThinkingIndicator inMessage />
  ) : lastSegment.kind !== 'steps' ? (
    <StreamingIndicator />
  ) : null;

  return (
    <div
      css={{
        padding: '4px 2px',
        display: 'flex',
        flexDirection: 'column',
        // Keep each step's text/tool block visually separated so two text
        // outputs around a tool call never run together.
        gap: 8,
        ...chatMsgFadein,
      }}
    >
      {segments.map((segment, index) => {
        switch (segment.kind) {
          case 'text':
            return (
              <StreamingMarkdown key={segment.key} content={segment.text} />
            );
          case 'file':
            return <FilePartView key={segment.key} part={segment.part} />;
          case 'card':
            return (
              <ChatToolCall
                key={segment.key}
                part={segment.part}
                onApprovalResponse={onApprovalResponse}
                isLastAssistant={isLastTurn}
                isLoading={isLoading}
                onComposeEmailResult={onComposeEmailResult}
                isMobile={isMobile}
              />
            );
          case 'steps':
            return (
              <ChatSteps
                key={segment.key}
                steps={segment.steps}
                isActive={isStreaming && index === segments.length - 1}
                countFromTurnStart={index === 0}
                onApprovalResponse={onApprovalResponse}
              />
            );
        }
      })}
      {trailingIndicator}
    </div>
  );
};

export const ChatMessage = React.memo(ChatMessageInner);

export { ThinkingIndicator } from '@modules/_shared/components/chat/ChatSteps';
